import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  Collapse,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { AppDispatch, RootReducerType } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  getDocumentsListThunk,
  getUploadedFileByIdThunk,
  saveRowUndertakingThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { setActiveStep } from "../redux/stepperROWSlice";
import { toast } from "react-toastify";
import UnderTackingListDetail, {
  getDocumentArray,
} from "./UnderTackingListDetail";
import { useNavigate } from "react-router-dom";
import { paymentThunk } from "@store/thunk/PaymentThunk";
import { StepsButton } from "./StepsButtons";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";
import { consentFormStepThunk } from "@modules/stepper/redux/thunk/documentThunk";

// Accordion-like Consent Card
export const UnderTakingConsentRow: React.FC<{
  collapsed: boolean;
  isDetailsPage?: boolean;
  setSubmitHandler?: (handler: () => Promise<void>) => void;
  setIsConsentValid?: (valid: boolean) => void;
  data?: any;
}> = ({
  collapsed,
  isDetailsPage,
  setSubmitHandler,
  setIsConsentValid,
  data,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    category_id,
    access_id,
    amenity,
    utility_type,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    district,
    state,
    national_highway_number,
    highway_category,
  } = useSelector((state: RootReducerType) => state.amenitiesROW);

  const { application_category_id } = useSelector(
    (state: RootReducerType) => state.accessLegacy,
  );

  const proposedUtilityAlignment = useSelector(
    (state: RootReducerType) =>
      state.applicationPreviewROW?.applicationPreview
        ?.proposed_utility_alignment,
  );

  const details = {
    start_chainage_km: data?.start_chainageKm || start_chainage_km,
    start_chainage_m: data?.start_chainageM || start_chainage_m,
    end_chainage_km: data?.end_chainageKm || end_chainage_km,
    end_chainage_m: data?.end_chainageM || end_chainage_m,
    district: data?.district || district,
    state: data?.state || state,
    national_highway_number:
      data?.national_highway_number || national_highway_number,
    highway_category: data?.highway_category || highway_category,
    proposed_utility_alignment:
      proposedUtilityAlignment || data?.proposed_utility_alignment,
  };

  const isLegacyContent = data?.is_legacy;

  const { activeStep } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );
  const { profile } = useSelector((state: RootReducerType) => state.profile);

  const [consentCheck, setConsentCheck] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [allDocuments, setAllDocuments] = useState<any[]>([]);

  // Memoized undertakings data
  const undertakings = useMemo(
    () => getDocumentArray(amenity, utility_type),
    [amenity, utility_type],
  );
  // Memoized access ID
  const currentAccessId = useMemo(
    () =>
      applicationEditId && applicationEditId !== 0
        ? applicationEditId
        : access_id,
    [applicationEditId, access_id],
  );

  // Create undertaking payload directly from undertakings array
  const createUndertakingPayload = useCallback(
    (accessId: number) => {
      return {
        application_id: accessId,
        undertaking_documents: undertakings.map((undertaking) => ({
          document_id: undertaking.document_id,
          document_name: undertaking.title,
          consent_text: undertaking.description,
        })),
      };
    },
    [undertakings],
  );

  // Fetch and merge documents
  const fetchAndMergeDocuments = useCallback(
    async (accessId: string | number) => {
      try {
        const [documentListData, uploadedFileById] = await Promise.all([
          dispatch(
            getDocumentsListThunk({
              category_id: isLegacyContent
                ? application_category_id
                : category_id,
              access_id: currentAccessId,
              document: "undertaking",
            }),
          ),
          dispatch(
            getUploadedFileByIdThunk({
              access_id: currentAccessId,
              category_id: isLegacyContent
                ? application_category_id
                : category_id,
              document: "undertaking",
            }),
          ),
        ]);

        const baseDocs = documentListData.payload.data || [];
        const uploadedFiles = uploadedFileById.payload.data?.files || [];

        const mergedDocs = baseDocs.map((doc: any) => {
          const uploadedFile = uploadedFiles.find(
            (file: any) => file.document_id === doc.document_id,
          );
          return uploadedFile ? { ...doc, uploadedFile } : doc;
        });

        const uploadedFileIds = new Set(
          uploadedFiles.map((file: any) => file.document_id),
        );
        const everyDocUploaded = baseDocs.every((doc: any) =>
          uploadedFileIds.has(doc.document_id),
        );

        setAllDocuments(mergedDocs);
        // setConsentCheck(everyDocUploaded);
      } catch (error) {
        console.error("Error fetching documents:", error);
        toast.error("Failed to load documents");
      }
    },
    [dispatch, category_id],
  );

  // Handle consent submission
  const handleConsent = async () => {
    try {
      const payload = createUndertakingPayload(currentAccessId);
      const res = await dispatch(saveRowUndertakingThunk(payload));

      if (res?.meta?.requestStatus === "fulfilled") {
        await fetchAndMergeDocuments(currentAccessId);
        setConsentCheck(true);
        toast.success("Undertakings & Consents saved successfully");
      } else {
        throw new Error("Failed to save undertaking document");
      }
    } catch (error) {
      toast.error("Failed to save undertaking document");
      setConsentCheck(false);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      dispatch(setActiveStep(5));
      dispatch(resetApplicationSubmitted());
      const res = await dispatch(
        consentFormStepThunk({ access_id: access_id as number }),
      );
      navigate("/stepper-row");
      // setTimeout(() => {
      //     navigate("/application-details-row");
      // }, 1000);
    } catch (error) {
      console.error("Error posting base64:", error);
      toast.error("An error occurred during submission");
    }
  };

  const backButtonClickHandler = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  // Initial data fetch
  useEffect(() => {
    if (currentAccessId) {
      fetchAndMergeDocuments(currentAccessId);
    }
  }, [currentAccessId, fetchAndMergeDocuments]);

  React.useEffect(() => {
    if (setIsConsentValid) {
      setIsConsentValid(consentCheck);
    }
  }, [consentCheck, setIsConsentValid]);

  return (
    <Box className="amenities-container" component="div">
      {!isDetailsPage && (
        <h3 className="stepper-hd w-full body-l flex items-center justify-between">
          Undertakings & Consents
        </h3>
      )}
      <Box
        className={!isDetailsPage ? `form-wrapper ` : `form-wrapper mt-4`}
        sx={{ paddingTop: 0 }}
      >
        {!isDetailsPage && (
          <div className="flex items-end justify-between w-full mb-4">
            <div>
              <h5 className="body-m gray-700 font-medium mt-3 mb-4">
                To continue, please review and give your consent for all
                undertakings below.
              </h5>
            </div>
          </div>
        )}
        <Card
          sx={{ boxShadow: "none", border: "1px solid #E7E8E9", p: 0, mb: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            className="py-2 bg-gray-100 px-4 m-0 flex items-center justify-between cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <h4 className="body-m gray-900 font-medium">
              Applicant Declarations
            </h4>
            <IconButton
              aria-label="Collapse section"
              sx={{ bgcolor: "transparent", p: 0, height: 28, width: 28 }}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <CardContent>
              <UnderTackingListDetail
                profile={profile}
                amenity={amenity || data?.amenity}
                utility_type={utility_type || data?.utility_type}
                details={details}
                proposed_utility_alignment={
                  proposedUtilityAlignment || data?.proposed_utility_alignment
                }
                legacyCheck={isLegacyContent}
              />
              {!isDetailsPage && (
                <Box className="px-4 py-1 mb-1" sx={{ bgcolor: "#F4F2F7" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={consentCheck}
                        checked={consentCheck}
                        onChange={handleConsent}
                        sx={{ "&.Mui-checked": { color: "#3A1C73" } }}
                      />
                    }
                    label="I/We hereby acknowledge and agree to abide by all the above terms, conditions, and undertakings."
                    sx={{
                      "& .MuiTypography-root": {
                        fontSize: "14px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      </Box>
      {!isDetailsPage && !isLegacyContent && (
        <Box className="stepper-btns gray:br-t-500 mt-3">
          <StepsButton
            onBack={backButtonClickHandler}
            onNext={handleSubmit}
            nextDisabled={!consentCheck}
          />
        </Box>
      )}
    </Box>
  );
};
